import type { ScheduledPostDto } from "@nlc/shared-types";
import type { Dayjs } from "dayjs";

import dayjs from "dayjs";
import { useMemo, useState } from "react";

import type { SwapDirection } from "../AnimatedSidePanel";
import type { CalendarView } from "./CalendarToolbar";
import type { ScheduledPost } from "./scheduledPost";

import { useScheduledPosts } from "../../hooks/useScheduledPosts";
import { AnimatedSidePanel } from "../AnimatedSidePanel";
import { CalendarToolbar } from "./CalendarToolbar";
import { FirstPostEmptyState } from "./FirstPostEmptyState";
import { MonthView } from "./MonthView";
import { PostPreview } from "./post-preview/PostPreview";
import { toScheduledPost } from "./scheduledPost";
import { WeekView } from "./WeekView";

export function ScheduleCalendar({
  onEditPost,
  onManageAccounts,
  onSchedulePost,
}: {
  onEditPost: (post: ScheduledPostDto) => void;
  onManageAccounts: () => void;
  onSchedulePost: () => void;
}) {
  const { data } = useScheduledPosts();
  const [brandIds, setBrandIds] = useState<number[]>([]);
  const postsByDate = useMemo(
    () =>
      (data?.posts ?? [])
        .filter((post) => post.status !== "canceled")
        .filter(
          (post) => brandIds.length === 0 || brandIds.includes(post.brand.id),
        )
        .map((post) => toScheduledPost(post))
        .sort((a, b) => a.scheduledAt.getTime() - b.scheduledAt.getTime()),
    [data, brandIds],
  );
  const [view, setView] = useState<CalendarView>("week");
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [selectedPost, setSelectedPost] = useState<ScheduledPost>();
  const [stepDirection, setStepDirection] = useState<SwapDirection>(0);

  function selectPost(post: ScheduledPost) {
    setStepDirection(0);
    setSelectedPost(post);
  }

  function filterByBrand(nextBrandIds: number[]) {
    setBrandIds(nextBrandIds);
    setSelectedPost(undefined);
  }

  function showWeek(day: Dayjs) {
    setDate(day);
    setView("week");
  }

  function stepSelectedPost(direction: -1 | 1) {
    const index = postsByDate.findIndex((post) => post.id === selectedPost?.id);
    const nextPost = postsByDate[index + direction];

    if (!nextPost) return;

    setStepDirection(direction);
    setSelectedPost(nextPost);
    setDate(dayjs(nextPost.scheduledAt));
  }

  function editPost(id: number) {
    const post = data?.posts.find((post) => post.id === id);

    if (post) onEditPost(post);
  }

  const hasNoPosts =
    data !== undefined &&
    data.posts.every((post) => post.status === "canceled");

  if (hasNoPosts) {
    return (
      <FirstPostEmptyState
        onManageAccounts={onManageAccounts}
        onSchedulePost={onSchedulePost}
      />
    );
  }

  return (
    <div className="flex flex-col gap-lg p-xl">
      <CalendarToolbar
        brandIds={brandIds}
        date={date}
        onBrandChange={filterByBrand}
        onNavigate={(direction) => setDate(date.add(direction, view))}
        onToday={() => setDate(dayjs())}
        onViewChange={setView}
        view={view}
      />
      <div className="flex items-start">
        <div className="min-w-0 flex-1 overflow-hidden rounded-xl border border-border-secondary bg-bg-primary">
          {view === "week" ? (
            <WeekView
              date={date}
              onSelectPost={selectPost}
              posts={postsByDate}
              selectedPostId={selectedPost?.id}
            />
          ) : (
            <MonthView
              date={date}
              onSelectPost={selectPost}
              onShowWeek={showWeek}
              posts={postsByDate}
              selectedPostId={selectedPost?.id}
            />
          )}
        </div>
        <AnimatedSidePanel
          contentKey={selectedPost?.id ?? "none"}
          direction={stepDirection}
          isOpen={selectedPost !== undefined}
        >
          {selectedPost && (
            <PostPreview
              onClose={() => setSelectedPost(undefined)}
              onEdit={() => editPost(selectedPost.id)}
              onStep={stepSelectedPost}
              post={selectedPost}
            />
          )}
        </AnimatedSidePanel>
      </div>
    </div>
  );
}
